/**
 * Bundle 44a - Per-user Data Quality card.
 *
 * Renders at the top of AdminUserDetailPage. Shows a plain-English
 * quality report (Name populated / Photos: 1 broken / DOB missing)
 * and offers a single "Repair" button that runs all applicable fixes
 * for THIS user only. Progress list appears while running.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, Loader2, Wrench } from 'lucide-react';
import api from '@/utils/api';

const STATE_ICON = {
  ok: { Icon: CheckCircle2, cls: 'text-emerald-600' },
  warn: { Icon: AlertTriangle, cls: 'text-amber-600' },
  error: { Icon: AlertCircle, cls: 'text-red-600' },
  info: { Icon: Info, cls: 'text-neutral-400' },
  repaired: { Icon: CheckCircle2, cls: 'text-emerald-600' },
  skipped: { Icon: Info, cls: 'text-neutral-400' },
};

const CheckRow = ({ check }) => {
  const { Icon, cls } = STATE_ICON[check.state] || STATE_ICON.info;
  return (
    <div
      className="flex items-start gap-2 py-1"
      data-testid={`data-quality-check-${check.field}`}
    >
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cls}`} />
      <div className="text-sm">
        <span className="font-medium text-neutral-800">{check.label}:</span>
        <span className="text-neutral-600 ml-1">{check.detail}</span>
      </div>
    </div>
  );
};

const StepRow = ({ step }) => {
  const state = step.state === 'repaired' ? 'repaired'
              : step.state === 'error' ? 'error'
              : step.state === 'ok' ? 'ok' : 'info';
  const { Icon, cls } = STATE_ICON[state] || STATE_ICON.info;
  return (
    <div
      className="flex items-start gap-2 py-1 text-sm"
      data-testid={`data-quality-step-${step.step}`}
    >
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cls}`} />
      <div>
        <span className="font-medium capitalize">{step.step.replace(/-/g, ' ')}:</span>
        <span className="text-neutral-600 ml-1">{step.message}</span>
      </div>
    </div>
  );
};

const DataQualityCard = ({ userId, onAfterRepair }) => {
  const [loading, setLoading] = useState(true);
  const [dq, setDq] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/data-quality`);
      setDq(data);
    } catch (e) {
      // Silent - fall back to hidden card if the user endpoint 404s.
      setDq(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const onRepair = async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      const { data } = await api.post(`/admin/users/${userId}/repair-all`);
      setRepairResult(data);
      toast.success(data.summary || 'Repair complete.');
      await load();
      if (onAfterRepair) onAfterRepair();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Repair failed.';
      toast.error(typeof msg === 'string' ? msg : 'Repair failed.');
    } finally {
      setRepairing(false);
    }
  };

  if (loading || !dq) return null;

  const badgeBg = dq.overall === 'error' ? 'bg-red-50 text-red-800 ring-red-100'
                : dq.overall === 'warn' ? 'bg-amber-50 text-amber-800 ring-amber-100'
                : 'bg-emerald-50 text-emerald-800 ring-emerald-100';

  return (
    <Card data-testid="admin-user-data-quality-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-semibold">
                This user&apos;s data
              </h3>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-4 ${badgeBg}`}
                data-testid="admin-user-data-quality-headline"
              >
                {dq.headline}
              </span>
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              {dq.counts?.ok || 0} healthy * {dq.counts?.warn || 0} warnings * {dq.counts?.error || 0} broken
            </div>
          </div>
          <Button
            size="sm"
            onClick={onRepair}
            disabled={repairing}
            data-testid="admin-user-data-quality-repair-btn"
            className="bg-primary-900 hover:bg-primary-800 text-white"
          >
            {repairing ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Repairing...</>
            ) : (
              <><Wrench className="h-3.5 w-3.5 mr-1.5" /> Repair now</>
            )}
          </Button>
        </div>

        <div className="mt-4 grid gap-1">
          {(dq.checks || []).map((c) => <CheckRow key={c.field} check={c} />)}
        </div>

        {repairResult ? (
          <div
            className="mt-5 rounded-md border border-neutral-200 bg-neutral-50 p-4"
            data-testid="admin-user-data-quality-repair-result"
          >
            <div className="text-sm font-semibold mb-2">
              {repairResult.summary}
            </div>
            <div className="grid gap-1">
              {(repairResult.steps || []).map((s) => (
                <StepRow key={s.step} step={s} />
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default DataQualityCard;
